// src/components/Summary.jsx

import React from 'react';
import { formatVisibleDate } from '../utils/dateHelpers';
import CreditType from '../components/CreditType';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import holidayJp from 'holiday-jp'; // npm install holiday-jp

dayjs.locale('ja');

/**
 * Summary は「表示専用」。
 * データ取得は ActorView 側で行い、summaryPayload を受け取って描画する。
 */
export default function Summary({
  actor,
  summaryPayload,
  loading = false,
  error = null,
}) {
  const data = summaryPayload?.summary || {};
  const norms = summaryPayload?.norms || [];

  // 横軸に表示する credit_type の一覧を収集
  const types = React.useMemo(() => {
    const allTypes = new Set();
    Object.values(data).forEach(dayObj => {
      if (!dayObj || typeof dayObj !== 'object') return;
      Object.keys(dayObj).forEach(type => allTypes.add(type));
    });
    return Array.from(allTypes);
  }, [data]);

  // 曜日と祝日判定
  const getDayClass = isoDate => {
    const d = dayjs(isoDate).startOf('day');
    const today = dayjs().startOf('day');
    const dow = d.day(); // 0=日,6=土
    const dayClass = [];
    if (d < today) dayClass.push('past');
    else if (d.diff(today) === 0) dayClass.push('today');
    else dayClass.push('future');
    if (dow === 6) dayClass.push('saturday');
    else if (dow === 0) dayClass.push('sunday');
    else if (holidayJp.isHoliday(d.toDate())) dayClass.push('holiday');
    return dayClass;
  };

  // 特定IDのカードへスクロール
  const handleScroll = (e, cardId) => {
    e.preventDefault();
    const element = document.getElementById(`card${cardId}`);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  // セルの文字列
  const getCellString = (dayData, type) => {
    let info = dayData?.[type];
    if (info == null || info === 0) return '-';
    if (type === '効率') {
      return (info > 0 ? '+' : '') + (100 * info).toFixed(0) + '%';
    }
    info = Math.round(info);
    const m = ('00' + (info % 60).toFixed(0)).slice(-2);
    return Math.floor(info / 60).toFixed(0) + ':' + m;
  };

  if (error) {
    return (
      <div className="error">
        <div>サマリーの取得に失敗しました。</div>
        <div style={{ fontSize: '0.9em', opacity: 0.7 }}>
          {String(error?.message || error)}
        </div>
      </div>
    );
  }

  if (loading && !summaryPayload) {
    return <div>読み込み中...</div>;
  }

  return (
    <>
      <table className="summary">
        <thead>
          <tr>
            <th>日付</th>
            {types.map(type => (
              <th key={type} className="type-header"><CreditType type={type} /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.keys(data).sort().map(date => {
            const dayData = data[date] || {};
            const dayClass = getDayClass(date);
            const normsClass = (norms.length > 0 && !dayClass.includes('past')) ? ['invalid'] : [];

            const dayInfos = types.map(type => ({
              type,
              desc: getCellString(dayData, type),
              clses: type === '効率' || !dayData[type] ? [] : ['reward'],
            }));

            return (
              <tr key={date} className={[...dayClass, ...normsClass].join(' ')}>
                <th className="day-header">{formatVisibleDate(date)}</th>
                {dayInfos.map(({ type, desc, clses }) => (
                  <td key={type} className={['day-cell', ...clses].join(' ')}>
                    {desc}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {norms.length > 0 && (
        <div className="norms">
          <label>ノルマ:</label>
          {norms.map(norm => (
            <span key={norm.id} className="norm">
              <a href="#" onClick={(e) => handleScroll(e, norm.id)}>{norm.content}</a>
            </span>
          ))}
        </div>
      )}
    </>
  );
}
